import { useEffect, useState } from "react";
import styles from "./DebugPanel.module.scss";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { BugButton } from "@/frontend/app/providers/ErrorBoundary";

const features: Feature[] = [];
const DEBUG_STORAGE_KEY = "debug-features";
const DEV_PANEL_STATUS_KEY = "debug-features-panel-status";
const availableFeatures = features.map((feature) => feature.name);
const DURATION_MIN = 30;

const featureFlags = atom(features);

export const useFeatureFlags = () => {
  return useAtomValue(featureFlags);
};

export const useSetFeatureFlags = () => {
  return useSetAtom(featureFlags);
};

type Feature = {
  name: string;
  enabled: boolean;
};

function getFeaturesStored() {
  const storedFeatures = localStorage.getItem(DEBUG_STORAGE_KEY);
  if (storedFeatures) {
    const existentFeatures = JSON.parse(storedFeatures).filter(({ name }: Feature) => availableFeatures.includes(name));
    const newFeatures = availableFeatures.filter(
      (name) => !existentFeatures.find((feature: Feature) => feature.name === name)
    );
    return [...existentFeatures, ...newFeatures.map((name) => ({ name, enabled: false }))];
  }
  return features;
}

export const DebugPanel = () => {
  const [isOn, setIsOn] = useState(false);
  const [features, setFeatures] = useState<Feature[]>(getFeaturesStored());
  const persist = useSetFeatureFlags();

  useEffect(() => {
    // @ts-ignore
    window["devmode"] = () =>
      setIsOn((prevIsOn) => {
        if (!prevIsOn) localStorage.setItem(DEV_PANEL_STATUS_KEY, new Date().getTime().toString());
        else localStorage.removeItem(DEV_PANEL_STATUS_KEY);
        return !prevIsOn;
      });

    const isInDev = localStorage.getItem(DEV_PANEL_STATUS_KEY);
    if (isInDev) {
      const diff = new Date().getTime() - parseInt(isInDev);
      if (diff < DURATION_MIN * 60 * 1000) setIsOn(true);
    }
  }, []);

  useEffect(() => {
    if (!isOn) return;
    setFeatures(getFeaturesStored());
  }, [isOn]);

  useEffect(() => {
    persist(features);
    localStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(features));
  }, [features]);

  if (!isOn) return;
  return (
    <div className={styles.panel}>
      <h4>Debug Panel</h4>
      <ul>
        {features.map((feature) => (
          <li key={feature.name}>
            <label>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={feature.enabled}
                onChange={(e) => {
                  setFeatures((prevFeatures) =>
                    prevFeatures.map((prevFeature) =>
                      prevFeature.name === feature.name ? { ...prevFeature, enabled: e.target.checked } : prevFeature
                    )
                  );
                }}
              />
              {feature.name}
            </label>
          </li>
        ))}
        <li key={"bug-button"}>
          <BugButton />
        </li>
      </ul>
    </div>
  );
};
